import React, { useState } from "react";
import { Settings2, Smartphone, Volume2 } from "lucide-react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { VoicemailOutlined } from "@mui/icons-material";
import { isTeamOrCustomPlan } from "../utils/frontChannelSettings";
import { TWILANSWER_APP_ACTION } from "../utils/forwardDeviceDestinations";

const ElevenLabsIcon = () => {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <div className="w-5 h-5 bg-indigo-500 rounded flex items-center justify-center text-white text-xs font-bold">
        11
      </div>
    );
  }
  return (
    <img
      src="/icons/elevenlabs.svg"
      alt="Eleven Labs"
      className="w-5 h-5"
      onError={() => setImgError(true)}
    />
  );
};

/**
 * Single source of truth for all call action options.
 * Add or remove actions here; set visibility per context so each modal shows the right list.
 *
 * Contexts:
 * - root: Primary route slot (ConfigureSlotModal)
 * - callMenuOption: Call menu option top-level (ConfigureCallActionModal — "Add Call Action" / "Change")
 * - callMenuBusinessHours: During business hours slot (ConfigureCallActionForBusinessHours)
 * - callMenuAfterHours: After hours slot (ConfigureCallActionForAfterHours)
 */
export const CALL_ACTION_OPTIONS = [
  {
    type: "ivr",
    title: "Business Hours",
    titleInRoot: "Call Menu (IVR)",
    subtitle: "Route differently during and after hours",
    subtitleInRoot: "Let callers choose with keypad",
    icon: <Settings2 className="h-6 w-6 text-indigo-500" />,
    showInRoot: true,
    showInCallMenuOption: true,
    showInCallMenuBusinessHours: false,
    showInCallMenuAfterHours: false,
  },
  {
    type: "forward",
    title: "Forward Call",
    subtitle: "Send the call to another number or the contact's assigned user",
    icon: <ArrowForwardIcon className="text-green-500" />,
    showInRoot: true,
    showInCallMenuOption: true,
    showInCallMenuBusinessHours: true,
    showInCallMenuAfterHours: true,
  },
  {
    type: TWILANSWER_APP_ACTION,
    title: "Twilanswer App",
    subtitle: "Ring a team member's Twilanswer mobile app",
    icon: <Smartphone className="h-6 w-6 text-green-500" />,
    showInRoot: true,
    showInCallMenuOption: true,
    showInCallMenuBusinessHours: true,
    showInCallMenuAfterHours: true,
  },
  {
    type: "forwardvwm",
    title: "Forward w/VM",
    subtitle: "Forward with voicemail fallback",
    icon: <ArrowForwardIcon className="text-green-500" />,
    showInRoot: false,
    showInCallMenuOption: false,
    showInCallMenuBusinessHours: false,
    showInCallMenuAfterHours: false,
  },
  {
    type: "dynamic_forward",
    title: "Dynamic Forward (beta)",
    subtitle: "Forward to the contact's assigned user",
    icon: <ArrowForwardIcon className="text-green-500" />,
    showInRoot: false,
    showInCallMenuOption: false,
    showInCallMenuBusinessHours: false,
    showInCallMenuAfterHours: false,
  },
  {
    type: "send_to_vm",
    title: "Send to Voicemail",
    subtitle: "Let callers leave a message",
    icon: <VoicemailOutlined className="text-indigo-500" />,
    showInRoot: true,
    showInCallMenuOption: true,
    showInCallMenuBusinessHours: true,
    showInCallMenuAfterHours: true,
  },
  {
    type: "play_recording",
    title: "Play Recording",
    subtitle: "Play a message to callers",
    icon: <Volume2 className="text-yellow-500" />,
    showInRoot: true,
    showInCallMenuOption: true,
    showInCallMenuBusinessHours: true,
    showInCallMenuAfterHours: true,
  },
  {
    type: "ai_agent",
    title: "Eleven Labs (beta)",
    subtitle: "Forward to Eleven Labs Voice Agent",
    icon: <ElevenLabsIcon />,
    showInRoot: true,
    showInCallMenuOption: true,
    showInCallMenuBusinessHours: true,
    showInCallMenuAfterHours: true,
  },
];

const CONTEXT_FLAGS = {
  root: "showInRoot",
  callMenuOption: "showInCallMenuOption",
  callMenuBusinessHours: "showInCallMenuBusinessHours",
  callMenuAfterHours: "showInCallMenuAfterHours",
};

/**
 * Returns call action options for a given context, with title/subtitle resolved (e.g. ivr uses titleInRoot in root).
 * @param {"root"|"callMenuOption"|"callMenuBusinessHours"|"callMenuAfterHours"} context
 * @returns {Array<{ type: string, title: string, subtitle: string, icon: React.ReactNode }>}
 */
export function getCallActionsForContext(context) {
  const flag = CONTEXT_FLAGS[context];
  if (!flag) return [];
  return CALL_ACTION_OPTIONS.filter((a) => a[flag]).map((a) => ({
    type: a.type,
    title: context === "root" && a.titleInRoot != null ? a.titleInRoot : a.title,
    subtitle: context === "root" && a.subtitleInRoot != null ? a.subtitleInRoot : a.subtitle,
    icon: a.icon,
  }));
}

export const DYNAMIC_FORWARD_DISABLED_NOTE = "Upgrade to the Team Plan to use Dynamic Forward.";

export const MULTI_FORWARD_DISABLED_NOTE =
  "Upgrade to the Team Plan to ring multiple destinations at once.";

/**
 * Dynamic Forward requires the Team or Custom plan.
 */
export function isDynamicForwardAvailable(planDetails) {
  return isTeamOrCustomPlan(planDetails);
}

/**
 * Simultaneous multi-destination forward requires the Team or Custom plan.
 */
export function isMultiForwardAvailable(planDetails) {
  return isTeamOrCustomPlan(planDetails);
}

/**
 * ElevenLabs requires recording and transcription to be enabled. Returns true when both are on.
 * Handles both plain boolean and DynamoDB .BOOL format.
 */
export function isElevenLabsAvailable(route) {
  if (!route) return false;
  const recordCall = route.recordCall?.BOOL ?? route.recordCall === true;
  const transcribeCall = route.transcribeCall?.BOOL ?? route.transcribeCall === true;
  return recordCall && transcribeCall;
}
